/**
 * TenantSwitcher - Tenant Selection Component
 *
 * For regular multi-tenant users: shows their accessible tenants.
 * For platform admins: loads the full tenant list from the admin API with
 * server-side search, plus an "All Tenants" aggregate option.
 *
 * Always list by display name. Slug is an immutable URL identifier (often the
 * original name) — keep it searchable, never show it as a subtitle.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { Building2, ChevronDown, Check, Search, Layers } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useTenant } from '../contexts/TenantContext'
import { useLazyPlatformTenantList } from '../features/home/context/DashboardTenantContext'
import { useDebounce } from '../hooks/useDebounce'
import type { Tenant } from '../types/tenant'
import { isBugsnapUiEvent } from '@features/bug-report/bugsnapUi'

interface TenantSwitcherProps {
  compact?: boolean
  readOnly?: boolean
  /** When true, shows an "All Tenants" aggregate option at the top of the dropdown (home page only). */
  showAggregateOption?: boolean
}

export const TenantSwitcher: React.FC<TenantSwitcherProps> = ({
  compact = false,
  readOnly = false,
  showAggregateOption = false,
}) => {
  const { t } = useTranslation()
  const { currentTenant, tenants, hasMultipleTenants, switchTenant, isLoading, isPlatformAdmin } =
    useTenant()

  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Platform admin: fetch full tenant list with server-side search.
  // Lazy: only fetch when the dropdown has been opened at least once.
  const [hasOpened, setHasOpened] = useState(false)
  const { tenants: adminTenants, isLoading: adminTenantsLoading } = useLazyPlatformTenantList(
    isPlatformAdmin && hasOpened,
    debouncedSearch,
  )

  // For regular users: client-side filter
  const filteredTenants = useMemo(() => {
    const activeTenants = (isPlatformAdmin ? adminTenants : tenants).filter(t => t.status === 'active')
    if (!search.trim()) return activeTenants
    const q = search.toLowerCase()
    return activeTenants.filter(
      t => t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q)
    )
  }, [isPlatformAdmin, adminTenants, tenants, search])

  const showSearch = isPlatformAdmin || tenants.length > 5

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Stay open while the user files a Bugsnap about this UI (see bugsnapUi).
      if (isBugsnapUiEvent(event.target, dropdownRef.current)) return
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearch('')
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      setTimeout(() => searchInputRef.current?.focus(), 50)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleTenantSelect = useCallback(
    async (tenant: Tenant | 'all') => {
      if (tenant === 'all') {
        await switchTenant('all')
        setIsOpen(false)
        setSearch('')
        return
      }
      if (tenant.id === currentTenant?.id) {
        setIsOpen(false)
        return
      }
      await switchTenant(tenant.id, tenant)
      setIsOpen(false)
      setSearch('')
    },
    [currentTenant, switchTenant]
  )

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: compact ? '4px 8px' : '8px 12px',
          backgroundColor: 'var(--surface-alt)',
          borderRadius: '8px',
          opacity: 0.6,
        }}
      >
        <Building2 size={compact ? 14 : 16} />
        <span style={{ fontSize: compact ? '12px' : '13px' }}>{t('common.loading')}</span>
      </div>
    )
  }

  // Platform admins always get the switcher; regular users only if they have multiple tenants
  const showDropdown = !readOnly && (isPlatformAdmin || hasMultipleTenants)

  if (!currentTenant && !isPlatformAdmin) return null

  const isAllTenants = isPlatformAdmin && !currentTenant
  const displayName = isAllTenants ? 'All Tenants' : (currentTenant?.name ?? '')

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => {
          if (!showDropdown) return
          if (!isOpen && !hasOpened) setHasOpened(true)
          setIsOpen(!isOpen)
        }}
        disabled={!showDropdown}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: compact ? '6px 10px' : '8px 12px',
          backgroundColor: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          cursor: showDropdown ? 'pointer' : 'default',
          color: 'var(--text)',
          fontSize: compact ? '12px' : '13px',
          fontWeight: 500,
          transition: 'all 0.15s ease',
          minWidth: compact ? 'auto' : '160px',
        }}
        title={isAllTenants ? 'All Tenants (aggregate view)' : `Current tenant: ${currentTenant!.name}`}
      >
        {isAllTenants
          ? <Layers size={compact ? 14 : 16} style={{ color: 'var(--primary)', flexShrink: 0 }} />
          : <Building2 size={compact ? 14 : 16} style={{ color: 'var(--primary)', flexShrink: 0 }} />
        }

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            overflow: 'hidden',
          }}
        >
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '120px',
            }}
          >
            {displayName}
          </span>
        </div>

        {showDropdown && (
          <ChevronDown
            size={14}
            style={{
              marginLeft: 'auto',
              transition: 'transform 0.15s ease',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        )}
      </button>

      {isOpen && showDropdown && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '4px',
            minWidth: '220px',
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
            <span
              style={{
                fontSize: '11px',
                color: 'var(--text-secondary)',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {t('tenant.switch')}
            </span>
          </div>

          {showSearch && (
            <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 8px',
                  backgroundColor: 'var(--surface-alt)',
                  borderRadius: '6px',
                }}
              >
                <Search size={13} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search tenants…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: '12px',
                    color: 'var(--text)',
                    width: '100%',
                  }}
                />
              </div>
            </div>
          )}

          <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
            {/* All Tenants aggregate option — only on the home page */}
            {showAggregateOption && isPlatformAdmin && (
              <button
                onClick={() => handleTenantSelect('all')}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  width: '100%', padding: '10px 12px',
                  border: 'none', borderBottom: '1px solid var(--border)',
                  backgroundColor: isAllTenants ? 'var(--surface-alt)' : 'transparent',
                  cursor: 'pointer', textAlign: 'left',
                  transition: 'background-color 0.15s ease', color: 'var(--text)',
                }}
                onMouseEnter={e => { if (!isAllTenants) e.currentTarget.style.backgroundColor = 'var(--surface-alt)' }}
                onMouseLeave={e => { if (!isAllTenants) e.currentTarget.style.backgroundColor = 'transparent' }}
              >
                <Layers size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: isAllTenants ? 600 : 400 }}>All Tenants</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Aggregate view</div>
                </div>
                {isAllTenants && <Check size={14} style={{ color: 'var(--primary)', flexShrink: 0 }} />}
              </button>
            )}

            {adminTenantsLoading && isPlatformAdmin && (
              <div
                style={{
                  padding: '12px',
                  fontSize: '12px',
                  color: 'var(--text-secondary)',
                  textAlign: 'center',
                }}
              >
                Loading…
              </div>
            )}

            {!adminTenantsLoading && filteredTenants.length === 0 && (
              <div
                style={{
                  padding: '12px',
                  fontSize: '12px',
                  color: 'var(--text-secondary)',
                  textAlign: 'center',
                }}
              >
                No tenants found
              </div>
            )}

            {filteredTenants.map(tenant => {
              const isActive = tenant.id === currentTenant?.id
              return (
                <button
                  key={tenant.id}
                  onClick={() => handleTenantSelect(tenant)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%',
                    padding: '10px 12px',
                    border: 'none',
                    backgroundColor: isActive ? 'var(--surface-alt)' : 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background-color 0.15s ease',
                    color: 'var(--text)',
                  }}
                  onMouseEnter={e => {
                    if (!isActive) e.currentTarget.style.backgroundColor = 'var(--surface-alt)'
                  }}
                  onMouseLeave={e => {
                    if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'
                  }}
                >
                  <Building2 size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <div
                      style={{
                        fontSize: '13px',
                        fontWeight: isActive ? 600 : 400,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {tenant.name}
                    </div>
                  </div>
                  {isActive && (
                    <Check size={14} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default TenantSwitcher
